import { createContext, useContext, useState, ReactNode } from 'react';
import { Notification, NotificationContainer } from '@flixbus/honeycomb-react';

interface Toast {
  message: string;
  status?: 'danger' | 'warning' | 'success';
}

interface NotificationContextType {
  // eslint-disable-next-line no-unused-vars
  pushToast: (toast: Toast) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [toasts, setToasts] = useState<Map<string, Toast>>(new Map());

  const pushToast = (toast: Toast) => {
    const key = Date.now().toString();
    setToasts(prev => new Map(prev).set(key, toast));
  };

  const removeToast = (key: string) => {
    setToasts(prev => {
      const newToasts = new Map(prev);
      newToasts.delete(key);
      return newToasts;
    });
  };

  return (
    <NotificationContext.Provider value={{ pushToast }}>
      {children}
      <NotificationContainer>
        {Array.from(toasts.entries()).map(([key, { message, status }]) => (
          <Notification
            toast
            appearance={status}
            key={key}
            closeProps={{
              'aria-label': 'Close',
              onClick: () => removeToast(key),
            }}
            dismissCallback={() => removeToast(key)}
          >
            {message}
          </Notification>
        ))}
      </NotificationContainer>
    </NotificationContext.Provider>
  );
};
